import { AppLayout, Flashbar } from '@amzn/awsui-components-console';
import React, { useContext, useEffect } from 'react';
import { HashRouter, Route } from 'react-router-dom';
import DeviceFarmHome from './DeviceFarmHome';
import SiteNavigation from './SiteNavigation';
import ManualTestingService from './manual-testing/Service';
import Stats from './metrics/Stats';
import QuickSightController from './metrics/analytics/Dashboard';
import HomeLabSetup from './home-lab-management/setup/Service';
import HomeLabMonitor from './home-lab-management/monitor/Service';
import HomeLabOffboard from './home-lab-management/offboard/Service';
import ApiTokens from './api-auth/api-tokens/Service';
import CreateApiTokens from './api-auth/api-tokens/create/Service';
import Testruns from './automation/testruns/Service';
import AutomationSetup from './automation/setup/Services';
import GroupsManager from './lab-management/group/Service';
import GroupDevicesManager from './lab-management/group/devices/Service';
import AssetsManager from './office-lab/assets/Service';
import CreateAsset from './office-lab/assets/create/Service';
import '../styles/footnote.scss';
import { getCurrentAuthUserAlias, getCurrentAuthUserEmail, isAdmin, isMidwayUser } from './lib/utils';
import ConfigureGroup from './lab-management/group/configure/Services';
import ProtectedRoute from './auth/ProtectedRoute';
import { RegionContext, RegionContextProvider } from './CustomContext';
import { NotificationProvider, useNotifications } from './NotificationContext';
import DeviceConfigManager from './lab-management/deviceConfig/Service';
import BotAccounts from './api-auth/bot-accounts/Service';
import OnboardBot from './api-auth/bot-accounts/onboard/Service';
import ConfigureBot from './api-auth/bot-accounts/configure/Service';
import LabManager from './lab-management/lab/Service';
import ConfigureLab from './lab-management/lab/configure/Service';
import OnboardLab from './lab-management/lab/onboard/Service';
import OffboardLab from './lab-management/lab/offboard/Service';
import AlarmRuleForm from './lab-management/lab/alarm-rules/AlarmRuleForm';
import OfficeLab from './office-lab/management/Service';
import ChatbotWidget from './chatbot/ChatbotWidget';
import { DrawerProvider, useDrawer } from './DrawerContext';
import CreateGroupPanel from './lab-management/group/create/CreateGroupPanel';
import DeleteGroupPanel from './lab-management/group/delete/DeleteGroupPanel';

export default function Layout() {
    return (
        <HashRouter>
            <RegionContextProvider>
                <NotificationProvider>
                    <DrawerProvider>
                        <AppLayoutWithNotifications />
                    </DrawerProvider>
                </NotificationProvider>
            </RegionContextProvider>
        </HashRouter>
    );
}

function DeleteGroupPanelWrapper() {
    const { drawerData } = useDrawer();
    return <DeleteGroupPanel group={drawerData.group || null} />;
}

function AppLayoutWithNotifications() {
    const { notifications } = useNotifications();
    const [navigationOpen, setNavigationOpen] = React.useState(false);
    const [is1pUser, setIs1pUser] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const { activeDrawerId, openDrawer, closeDrawer } = useDrawer();

    const DRAWER_CONFIGS = {
        'create-group': {
            id: 'create-group',
            content: <CreateGroupPanel />,
            trigger: { iconName: 'add-plus' },
            ariaLabels: { drawerName: 'Create group' },
            defaultSize: 350,
        },
        'delete-group': {
            id: 'delete-group',
            content: <DeleteGroupPanelWrapper />,
            trigger: { iconName: 'remove' },
            ariaLabels: { drawerName: 'Delete group' },
            defaultSize: 350,
        },
    };

    const waitForLoomMetrics = (user, maxAttempts = 10, interval = 1000) => {
        return new Promise((resolve, reject) => {
            let attempts = 0;

            const checkLoomMetrics = () => {
                attempts++;
                if (typeof window.initLoomMetricsService === 'function') {
                    try {
                        const result = window.initLoomMetricsService(user);
                        console.log('initLoomMetricsService', result);
                        resolve(result);
                    } catch (e) {
                        console.error('Fail to initialize loom metrics', e);
                        reject(e);
                    }
                } else if (attempts >= maxAttempts) {
                    console.error('Loom metrics service not available after maximum attempts');
                    reject(new Error('Loom metrics service not available'));
                } else {
                    setTimeout(checkLoomMetrics, interval);
                }
            };

            checkLoomMetrics();
        });
    };

    React.useEffect(() => {
        (async function () {
            setLoading(true);
            try {
                // Check if user is authenticated via Midway
                const isMidway = isMidwayUser();
                console.log(`User is from Midway: ${isMidway}`);

                if (isMidway) {
                    setIs1pUser(true);
                    const currentAuthUser = await getCurrentAuthUserAlias();
                    console.log(`Current Midway user's alias is ${currentAuthUser}`);
                    if (window.location.protocol === 'https:') {
                        await waitForLoomMetrics(currentAuthUser);
                    }
                } else {
                    setIs1pUser(false);
                    console.log('User is not from Midway (3P user)');
                }
            } catch (e) {
                console.error("Fail to get current auth user's identity", e);
            }
            setLoading(false);
        })();
    }, []);
    return (
        <>
            <AppLayout
                content={
                    <div>
                        <Flashbar
                            items={notifications}
                            stackItems
                            i18nStrings={{
                                ariaLabel: 'Notifications',
                                notificationBarAriaLabel: 'View all notifications',
                                notificationBarText: 'Notifications',
                                errorIconAriaLabel: 'Error',
                                warningIconAriaLabel: 'Warning',
                                successIconAriaLabel: 'Success',
                                infoIconAriaLabel: 'Info',
                                inProgressIconAriaLabel: 'In progress',
                            }}
                        />
                        <Route exact path="/" component={DeviceFarmHome} />
                        <Route exact path="/device-checkout/home" component={ManualTestingService} />
                        <Route exact path="/metrics/metadata" component={Stats} />
                        <Route exact path="/metrics/analytics" component={QuickSightController} />
                        {!loading && (
                            <ProtectedRoute isProtected={is1pUser}>
                                <Route exact path="/home-lab-management/setup" component={HomeLabSetup} />
                                <Route exact path="/home-lab-management/monitor" component={HomeLabMonitor} />
                                <Route exact path="/home-lab-management/offboarding" component={HomeLabOffboard} />
                            </ProtectedRoute>
                        )}
                        <Route exact path="/api-auth/personal-tokens" component={ApiTokens} />
                        <Route exact path="/api-auth/personal-tokens/create" component={CreateApiTokens} />
                        <Route exact path="/automation/test-runs" component={Testruns} />
                        <Route exact path="/automation/setup" component={AutomationSetup} />
                        <Route exact path="/api-auth/bot-accounts" component={BotAccounts} />
                        <Route exact path="/api-auth/bot-accounts/onboard" component={OnboardBot} />
                        <Route
                            exact
                            path="/api-auth/bot-accounts/configure/:id"
                            component={(props) => <ConfigureBot {...props} />}
                        />
                        <Route exact path="/lab-management/labs" component={LabManager} />
                        <Route exact path="/lab-management/labs/onboard" component={OnboardLab} />
                        <Route
                            exact
                            path="/lab-management/labs/configure/:id/alarm-rules"
                            component={(props) => <AlarmRuleForm {...props} />}
                        />
                        <Route
                            exact
                            path="/lab-management/labs/configure/:id/alarm-rules/:ruleIndex"
                            component={(props) => <AlarmRuleForm {...props} />}
                        />
                        <Route
                            exact
                            path="/lab-management/labs/configure/:id"
                            component={(props) => <ConfigureLab {...props} />}
                        />
                        <Route
                            exact
                            path="/lab-management/labs/offboard/:id"
                            component={(props) => <OffboardLab {...props} />}
                        />
                        <Route exact path="/lab-management/groups" component={GroupsManager} />
                        <Route exact path="/lab-management/groups/:groupId/devices" component={GroupDevicesManager} />
                        <Route
                            exact
                            path="/lab-management/groups/configure/:id"
                            component={(props) => <ConfigureGroup {...props} />}
                        />
                        <Route exact path="/lab-management/device-configs" component={DeviceConfigManager} />
                        <Route exact path="/office-lab/management" component={OfficeLab} />
                        <Route exact path="/office-lab/assets" component={AssetsManager} />
                        <Route exact path="/office-lab/assets/create" component={CreateAsset} />
                    </div>
                }
                navigation={<SiteNavigation activeHref="#/" is1pUser={is1pUser} />}
                navigationOpen={navigationOpen}
                onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
                toolsHide={true}
                drawers={activeDrawerId && DRAWER_CONFIGS[activeDrawerId] ? [DRAWER_CONFIGS[activeDrawerId]] : []}
                activeDrawerId={activeDrawerId}
                onDrawerChange={({ detail }) => {
                    if (detail.activeDrawerId === null) {
                        closeDrawer();
                    }
                }}
            />
            {/* Add Chatbot Widget - Available on all pages */}
            <ChatbotWidget />
        </>
    );
}
